import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import type { HighlightKey } from '../../content/types';
import type { Sample } from '../../simulation/types';
import { parseMarkup, stripMarkup } from '../Markup';

export interface PlotSeries {
  key: string;
  label: string;
  /** CSS color (use a token variable). */
  color: string;
  /** SVG stroke-dasharray; distinguishes series without relying on color. */
  dash?: string;
  width?: number;
  get: (s: Sample) => number;
  highlight?: HighlightKey;
}

export interface PlotTrace {
  id: string;
  /** Legend prefix for comparisons, e.g. "Run 3". */
  label?: string;
  samples: Sample[];
  /** Overrides the series dash for comparison runs. */
  dash?: string;
  /** Overrides the series colour for comparison runs (applies to the first series only). */
  color?: string;
  opacity?: number;
}

interface Props {
  title: string;
  unit: string;
  series: PlotSeries[];
  traces: PlotTrace[];
  tMax: number;
  cursorT: number | null;
  onCursor: (t: number | null) => void;
  /** Horizontal reference lines (e.g. $T_{max}$). */
  refLines?: { y: number; label: string }[];
  /** Shade intervals where this returns non-zero (saturation). */
  shade?: (s: Sample) => number;
  shadeLabel?: string;
  height?: number;
  highlight?: HighlightKey | null;
  /** Fix the y-range (used to keep comparison axes identical). */
  yDomain?: [number, number];
  digits?: number;
}

const PAD = { l: 48, r: 10, t: 8, b: 22 };

function niceTicks(lo: number, hi: number, count = 4): number[] {
  const span = hi - lo || 1;
  const step0 = span / count;
  const mag = Math.pow(10, Math.floor(Math.log10(step0)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => span / s <= count + 0.5) ?? 10 * mag;
  const ticks: number[] = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi + 1e-9; v += step) ticks.push(Math.abs(v) < 1e-12 ? 0 : v);
  return ticks;
}

export function domainFor(series: PlotSeries[], traces: PlotTrace[], refLines: { y: number }[] = []): [number, number] {
  let lo = Infinity;
  let hi = -Infinity;
  for (const tr of traces)
    for (const s of tr.samples)
      for (const se of series) {
        const v = se.get(s);
        if (Number.isFinite(v)) {
          if (v < lo) lo = v;
          if (v > hi) hi = v;
        }
      }
  for (const r of refLines) {
    lo = Math.min(lo, r.y);
    hi = Math.max(hi, r.y);
  }
  if (!Number.isFinite(lo)) return [-1, 1];
  if (hi - lo < 1e-6) {
    const c = (hi + lo) / 2;
    const pad = Math.max(Math.abs(c) * 0.05, 0.05);
    return [c - pad, c + pad];
  }
  const pad = (hi - lo) * 0.08;
  return [lo - pad, hi + pad];
}

function nearest(samples: Sample[], t: number): Sample | null {
  if (!samples.length) return null;
  let lo = 0;
  let hi = samples.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (samples[mid].t < t) lo = mid;
    else hi = mid;
  }
  return Math.abs(samples[lo].t - t) <= Math.abs(samples[hi].t - t) ? samples[lo] : samples[hi];
}

function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [w, setW] = useState(480);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => setW(Math.max(200, entries[0].contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

export function TimePlot(props: Props) {
  const { title, unit, series, traces, tMax, cursorT, onCursor, refLines = [], shade, shadeLabel, height = 120, highlight, digits = 2 } = props;
  const [wrapRef, width] = useWidth<HTMLDivElement>();
  const descId = useId();
  const [y0, y1] = props.yDomain ?? domainFor(series, traces, refLines);
  const plotW = width - PAD.l - PAD.r;
  const plotH = height - PAD.t - PAD.b;
  const x = (t: number) => PAD.l + (t / (tMax || 1)) * plotW;
  const y = (v: number) => PAD.t + (1 - (v - y0) / (y1 - y0 || 1)) * plotH;

  const paths = useMemo(() => {
    const out: { key: string; d: string; color: string; dash?: string; width: number; opacity: number; dim: boolean; lit: boolean }[] = [];
    const maxPts = Math.max(50, Math.floor(plotW));
    for (const tr of traces) {
      const stride = Math.max(1, Math.floor(tr.samples.length / maxPts));
      for (const se of series) {
        let d = '';
        for (let i = 0; i < tr.samples.length; i += stride) {
          const s = tr.samples[i];
          const v = se.get(s);
          if (!Number.isFinite(v)) continue;
          const yy = Math.min(Math.max(y(v), PAD.t - 4), PAD.t + plotH + 4);
          d += `${d ? 'L' : 'M'}${x(s.t).toFixed(1)},${yy.toFixed(1)}`;
        }
        const last = tr.samples[tr.samples.length - 1];
        if (last && tr.samples.length % stride !== 1 && stride > 1) d += `L${x(last.t).toFixed(1)},${y(se.get(last)).toFixed(1)}`;
        // Dim the other series only when this plot contains the highlighted signal.
        const dim = !!highlight && se.highlight !== highlight && series.some((q) => q.highlight === highlight);
        const lit = !!highlight && se.highlight === highlight;
        const primary = se === series[0];
        out.push({
          key: `${tr.id}-${se.key}`,
          d,
          color: lit ? 'var(--hl-ink)' : primary && tr.color ? tr.color : se.color,
          dash: primary ? (tr.dash ?? se.dash) : se.dash,
          width: (se.width ?? 1.6) * (lit ? 2.4 : 1),
          opacity: tr.opacity ?? 1,
          dim,
          lit,
        });
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [traces, series, plotW, plotH, y0, y1, tMax, highlight]);

  const bands = useMemo(() => {
    if (!shade || traces.length !== 1) return [];
    const xs = traces[0].samples;
    const out: { t0: number; t1: number; sign: number }[] = [];
    let start: number | null = null;
    let sign = 0;
    for (let i = 0; i < xs.length; i++) {
      const v = shade(xs[i]);
      if (v !== 0 && start === null) {
        start = xs[i].t;
        sign = v;
      } else if ((v === 0 || v !== sign) && start !== null) {
        out.push({ t0: start, t1: xs[i].t, sign });
        start = v !== 0 ? xs[i].t : null;
        sign = v;
      }
    }
    if (start !== null && xs.length) out.push({ t0: start, t1: xs[xs.length - 1].t, sign });
    return out;
  }, [shade, traces]);

  const tFromEvent = (e: PointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left;
    return Math.min(tMax, Math.max(0, ((px - PAD.l) / plotW) * tMax));
  };

  const onKey = (e: KeyboardEvent<SVGSVGElement>) => {
    const step = e.shiftKey ? 1 : 0.1;
    const cur = cursorT ?? 0;
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = Math.min(tMax, cur + step);
    else if (e.key === 'ArrowLeft') next = Math.max(0, cur - step);
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tMax;
    else if (e.key === 'Escape') {
      onCursor(null);
      return;
    }
    if (next !== null) {
      e.preventDefault();
      onCursor(Math.round(next * 100) / 100);
    }
  };

  const summary = useMemo(() => summarize(title, unit, series, traces, digits), [title, unit, series, traces, digits]);
  const yTicks = niceTicks(y0, y1, Math.max(2, Math.floor(plotH / 30)));
  const xTicks = niceTicks(0, tMax, Math.max(3, Math.floor(plotW / 70)));
  const hasHighlight = !!highlight && series.some((se) => se.highlight === highlight);

  return (
    <figure className={`plot${hasHighlight ? ' is-hl' : ''}`} ref={wrapRef} data-hl={hasHighlight ? highlight ?? undefined : undefined}>
      <figcaption className="plot-head">
        <span className="plot-title">
          {title} <span className="muted">({unit})</span>
        </span>
        <span className="legend">
          {series.map((se) => (
            <span key={se.key} className={`legend-item${highlight && se.highlight === highlight ? ' is-hl' : ''}`}>
              <svg width="22" height="8" aria-hidden="true">
                <line
                  x1="0"
                  y1="4"
                  x2="22"
                  y2="4"
                  stroke={highlight && se.highlight === highlight ? 'var(--hl-ink)' : se.color}
                  strokeWidth={2}
                  strokeDasharray={se.dash}
                />
              </svg>
              {parseMarkup(se.label, se.key)}
            </span>
          ))}
          {shadeLabel && bands.length > 0 && (
            <span className="legend-item">
              <span className="shade-swatch" aria-hidden="true" />
              {shadeLabel}
            </span>
          )}
        </span>
      </figcaption>
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={`${title} plot. Focus and use left and right arrow keys to move the time cursor.`}
        aria-describedby={descId}
        tabIndex={0}
        className="plot-svg"
        onKeyDown={onKey}
        onPointerMove={(e) => (e.pointerType === 'mouse' || e.buttons) && onCursor(Math.round(tFromEvent(e) * 100) / 100)}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture?.(e.pointerId);
          onCursor(Math.round(tFromEvent(e) * 100) / 100);
        }}
      >
        <rect x={PAD.l} y={PAD.t} width={plotW} height={plotH} className="plot-bg" />
        {bands.map((b, i) => (
          <rect key={i} x={x(b.t0)} y={PAD.t} width={Math.max(1, x(b.t1) - x(b.t0))} height={plotH} className="plot-shade">
            <title>Saturated {b.sign > 0 ? 'high' : 'low'}</title>
          </rect>
        ))}
        {bands.map((b, i) => (
          <text key={`m${i}`} x={x(b.t0) + 1} y={PAD.t + 9} className="plot-shade-mark" aria-hidden="true">
            {b.sign > 0 ? '▲' : '▼'}
          </text>
        ))}
        {yTicks.map((v) => (
          <g key={`y${v}`}>
            <line x1={PAD.l} x2={PAD.l + plotW} y1={y(v)} y2={y(v)} className="grid" />
            <text x={PAD.l - 4} y={y(v) + 3} className="tick" textAnchor="end">
              {Math.abs(v) >= 100 ? v.toFixed(0) : +v.toFixed(3)}
            </text>
          </g>
        ))}
        {xTicks.map((t) => (
          <text key={`x${t}`} x={x(t)} y={height - 6} className="tick" textAnchor="middle">
            {+t.toFixed(2)}
          </text>
        ))}
        <text x={PAD.l + plotW} y={height - 6} className="tick" textAnchor="end" dx={-2} dy={-10}>
          t (s)
        </text>
        {refLines.map((r, i) => (
          <g key={`ref-${i}`}>
            <line x1={PAD.l} x2={PAD.l + plotW} y1={y(r.y)} y2={y(r.y)} className="ref-line" />
            <foreignObject x={PAD.l + plotW - 110} y={y(r.y) - 14} width={108} height={16}>
              <div className="ref-label">{parseMarkup(r.label, `ref${i}`)}</div>
            </foreignObject>
          </g>
        ))}
        {paths.map((p) => (
          <path
            key={p.key}
            d={p.d}
            fill="none"
            stroke={p.color}
            strokeWidth={p.width}
            strokeDasharray={p.dash}
            strokeLinejoin="round"
            opacity={p.dim ? 0.3 : p.opacity}
            style={p.lit ? { filter: 'drop-shadow(0 0 4px var(--hl))' } : undefined}
          />
        ))}
        {cursorT !== null && (
          <line x1={x(cursorT)} x2={x(cursorT)} y1={PAD.t} y2={PAD.t + plotH} className="cursor-line" />
        )}
      </svg>
      <div className={`plot-readout${cursorT === null ? ' is-empty' : ''}`} aria-hidden={cursorT === null}>
        <span>t = {cursorT !== null ? `${cursorT.toFixed(2)} s` : '- s'}</span>
        {traces.map((tr) =>
          series.map((se) => {
            const s = cursorT !== null ? nearest(tr.samples, cursorT) : null;
            return (
              <span key={`${tr.id}-${se.key}`}>
                {tr.label ? `${tr.label} ` : ''}
                {parseMarkup(se.label, `ro-${se.key}`)} = {s ? se.get(s).toFixed(digits) : '-'}
              </span>
            );
          }),
        )}
      </div>
      <p id={descId} className="sr-only">
        {summary}
      </p>
    </figure>
  );
}

function summarize(title: string, unit: string, series: PlotSeries[], traces: PlotTrace[], digits: number): string {
  const parts: string[] = [];
  for (const tr of traces) {
    if (!tr.samples.length) continue;
    for (const se of series) {
      let min = Infinity;
      let max = -Infinity;
      let tMin = 0;
      let tMax = 0;
      for (const s of tr.samples) {
        const v = se.get(s);
        if (v < min) {
          min = v;
          tMin = s.t;
        }
        if (v > max) {
          max = v;
          tMax = s.t;
        }
      }
      const first = se.get(tr.samples[0]);
      const last = tr.samples[tr.samples.length - 1];
      parts.push(
        `${tr.label ? tr.label + ' ' : ''}${stripMarkup(se.label)}: starts at ${first.toFixed(digits)}, minimum ${min.toFixed(digits)} at ${tMin.toFixed(1)} s, maximum ${max.toFixed(digits)} at ${tMax.toFixed(1)} s, ${last.t.toFixed(1)} s value ${se.get(last).toFixed(digits)} ${unit}.`,
      );
    }
  }
  return parts.length ? `${title}. ${parts.join(' ')}` : `${title}: no data yet.`;
}
